import { useState } from 'react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/appointments/v3/model.pb';
import { AppointmentsApiQueries } from '@frontend/api-appointments';
import { PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Modal, ModalControlModalProps, SpinningLoader } from '@frontend/design-system';
import { AppointmentsList } from '../appointments-list';

type AppointmentSelectorModalProps = {
  groupId: string;
  personId: string;
  includePast?: boolean;
  onSelect: (appointment: Appointment) => void;
  onSkip: () => void;
  onClose: () => void;
  appointments?: Appointment[];
  isLoading?: boolean;
};
type FullModalProps = ModalControlModalProps & AppointmentSelectorModalProps;

export const AppointmentSelectorModal = ({
  groupId,
  personId,
  includePast,
  onSelect,
  onSkip,
  ...rest
}: FullModalProps) => {
  return (
    <Modal
      css={{
        minWidth: 600,
      }}
      {...rest}
    >
      <AppointmentSelectorModalContents
        groupId={groupId}
        personId={personId}
        includePast={includePast}
        onSelect={onSelect}
        onSkip={onSkip}
        onClose={rest.onClose}
      />
    </Modal>
  );
};

export const AppointmentSelectorModalContents = ({
  groupId,
  personId,
  includePast,
  onSelect,
  onSkip,
  onClose,
  appointments,
  isLoading,
}: AppointmentSelectorModalProps) => {
  const { t } = useTranslation('appointment-selector');
  const personQuery = PersonsV3.PersonQueries.useGetPersonLegacyQuery({
    personId,
    locationIds: [groupId],
  });
  const personName = personQuery.data?.preferredName || personQuery.data?.firstName;
  const appointmentsQuery = AppointmentsApiQueries.useListPersonAppointments(
    {
      locationId: groupId,
      personId,
      includePast,
    },
    {
      enabled: appointments === undefined,
    }
  );
  const resolvedAppointments = appointments ?? appointmentsQuery.data?.appointments ?? [];
  const resolvedIsLoading = isLoading ?? appointmentsQuery.isLoading;

  return (
    <>
      <Modal.Header onClose={onClose}>
        {personName
          ? t("{{personName}}'s Upcoming Appointments", {
              personName,
            })
          : t('Upcoming Appointments')}
      </Modal.Header>
      <Modal.Label>{t('Select an appointment')}</Modal.Label>
      <Modal.Body css={{ width: '100%', maxHeight: 500 }}>
        {resolvedIsLoading ? (
          <SpinningLoader />
        ) : (
          <AppointmentsList appointments={resolvedAppointments} onSelect={onSelect} />
        )}
      </Modal.Body>
      <Modal.Actions secondaryLabel={t('Skip')} onSecondaryClick={onSkip} showPrimary={false} />
    </>
  );
};

export const AppointmentSelectorModalContentsV2 = ({
  groupId,
  personId,
  includePast,
  onSelect,
  onClose,
  appointments,
  isLoading,
  onBack,
}: AppointmentSelectorModalProps & { onBack: () => void }) => {
  const { t } = useTranslation('appointment-selector');
  const [appointmentSelected, setAppointmentSelected] = useState<Appointment | null>(null);
  const isLargeMode = useMatchMedia({ minWidth: breakpoints.xsmall.max });
  const appointmentsQuery = AppointmentsApiQueries.useListPersonAppointments(
    {
      locationId: groupId,
      personId,
      includePast,
    },
    {
      enabled: appointments === undefined,
    }
  );
  const resolvedAppointments = appointments ?? appointmentsQuery.data?.appointments ?? [];
  const resolvedIsLoading = isLoading ?? appointmentsQuery.isLoading;

  return (
    <>
      <Modal.Header onClose={onClose}>
        <Button iconName='back-small' onClick={onBack} label='back' variant='secondary' />
        {t('Appointments')}
      </Modal.Header>
      <Modal.Body css={{ width: '100%', marginBottom: theme.spacing(10) }}>
        {resolvedIsLoading ? (
          <div css={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <SpinningLoader />
          </div>
        ) : (
          <AppointmentsList
            appointments={resolvedAppointments}
            onSelect={(appointment) => setAppointmentSelected(appointment)}
          />
        )}
      </Modal.Body>
      <Modal.StickyFooter css={isLargeMode && { padding: theme.spacing(4), justifyContent: 'flex-end' }}>
        <Button
          onClick={() => {
            if (appointmentSelected) onSelect(appointmentSelected);
          }}
          disabled={!appointmentSelected}
        >
          {t('Insert')}
        </Button>
      </Modal.StickyFooter>
    </>
  );
};
